import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { logger } from './logger';

/**
 * Detect Supabase / edge-function auth errors so the app can sign the user out
 * and redirect to /login automatically instead of leaving them on a page with
 * a dead session that only fails when they submit a form.
 */
function isAuthError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  if (error instanceof FunctionsHttpError) {
    const ctx = error.context as Response | undefined;
    return ctx?.status === 401;
  }

  if ('status' in error && (error as { status: unknown }).status === 401) {
    return true;
  }

  if ('message' in error && typeof (error as { message: unknown }).message === 'string') {
    const msg = (error as { message: string }).message.toLowerCase();
    if (
      msg.includes('jwt expired') ||
      msg.includes('jwt is invalid') ||
      msg.includes('invalid jwt') ||
      msg.includes('session refresh failed')
    ) {
      return true;
    }
  }

  return false;
}

let signingOut = false;
function handleAuthFailure(): void {
  if (signingOut) return;
  signingOut = true;
  logger.warn('Authentication error detected; signing out and redirecting to login');
  supabase.auth
    .signOut({ scope: 'local' })
    .catch((err) => logger.warn('signOut after auth failure threw', { error: String(err) }))
    .finally(() => {
      // Reset shortly after the auth state change has propagated so a future
      // expiry can still trigger this handler.
      setTimeout(() => {
        signingOut = false;
      }, 1000);
    });
}

const handleError = (error: unknown): void => {
  if (isAuthError(error)) handleAuthFailure();
};

/**
 * Default QueryClient configuration for production use
 *
 * This configuration provides:
 * - Optimized caching strategy (5min stale, 10min garbage collection)
 * - Smart retry logic (avoid retrying 4xx errors)
 * - Performance-focused settings for production workloads
 */
const defaultQueryClientOptions = {
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      // Safari background-tab throttling kills the Supabase HTTP/2 connection
      // and stalls auto-refresh; if React Query refetches on focus/reconnect
      // before the session is re-validated, every request fails with
      // "Load failed" and retries exhaust over ~7s of broken UI. Refetches
      // are triggered explicitly from useAuth's validateOnFocus after a
      // successful refreshSession().
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: (failureCount: number, error: unknown) => {
        // Don't retry on 4xx errors
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        return failureCount < 3;
      },
    },
    mutations: {
      retry: false, // Don't retry mutations by default
    },
  },
};

function buildQueryClient(): QueryClient {
  return new QueryClient({
    ...defaultQueryClientOptions,
    queryCache: new QueryCache({ onError: handleError }),
    mutationCache: new MutationCache({ onError: handleError }),
  });
}

/**
 * Creates a QueryClient instance configured for production use
 *
 * Use this for the main application, ensuring consistent query behavior
 * across the app with optimized caching and retry logic.
 */
export function createAppQueryClient(): QueryClient {
  return buildQueryClient();
}

